import { createFileRoute, Link } from "@tanstack/react-router";
import { Layers, Bot, Database } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Breadcrumb, FinalCta, Card } from "./integracoes";

const SITE = "https://kentro.com.br";

/**
 * Perguntas exclusivas de omnichannel (operação de canais unificados).
 * A lista completa (planos, LGPD, integrações, segmentos) fica em /perguntas-frequentes.
 */
const OMNICHANNEL_FAQ: { id: string; q: string; a: string }[] = [
  {
    id: "faq-historico-entre-canais",
    q: "O histórico do cliente continua o mesmo se ele trocar de canal?",
    a: "Sim. O contato é o mesmo registro no CRM, então o atendente vê as conversas anteriores de WhatsApp, Instagram, Facebook, WebChat e e-mail na mesma ficha, sem pedir que o cliente repita a história.",
  },
  {
    id: "faq-um-numero-varios-atendentes",
    q: "Vários atendentes podem trabalhar no mesmo número de WhatsApp?",
    a: "Sim. Um único número atende toda a equipe, com distribuição automática das conversas por fila e limite de atendimentos simultâneos por agente.",
  },
  {
    id: "faq-canais-unificados",
    q: "Quais canais entram na mesma tela?",
    a: "WhatsApp (API Oficial da Meta), Instagram, Facebook, Telegram, WebChat, e-mail e voz/PABX, cada um configurado como fila própria com regras, agentes e relatórios independentes.",
  },
  {
    id: "faq-transferencia-sem-perder-contexto",
    q: "Como funciona a transferência entre setores sem perder o contexto?",
    a: "A conversa é transferida entre agentes e filas levando o histórico e as anotações internas, e a troca fica registrada no atendimento para auditoria posterior.",
  },
  {
    id: "faq-medir-resultado-omnichannel",
    q: "Como medir se a estratégia omnichannel está funcionando?",
    a: "Pelos relatórios da plataforma: tempo médio de atendimento, cumprimento de SLA por fila, volume por canal, pesquisa de qualidade e histórico completo por protocolo, com exportação para análise externa.",
  },
  {
    id: "faq-migrar-de-varias-ferramentas",
    q: "Já uso ferramentas diferentes para cada canal. Como fica a migração?",
    a: "Cada canal passa a ser uma fila na Kentro e pode ser ativado por etapas, começando pelo canal de maior volume. Sistemas atuais continuam conectados por integrações nativas ou por API, Webhooks e SDK.",
  },
];

export const Route = createFileRoute("/omnichannel")({
  head: () => ({
    meta: [
      { title: "Estratégia Omnichannel: O Guia para Transformar seu Negócio" },
      {
        name: "description",
        content:
          "Entenda o que é omnichannel, por que importa e como unificar WhatsApp, Instagram e e-mail numa só plataforma com IA e CRM. Guia completo da Kentro.",
      },
      { property: "og:title", content: "Estratégia Omnichannel: O Guia para Transformar seu Negócio" },
      {
        property: "og:description",
        content: "Como aplicar uma estratégia omnichannel real com WhatsApp, Instagram, e-mail e voz unificados.",
      },
      { property: "og:url", content: `${SITE}/omnichannel` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Estratégia Omnichannel — Kentro" },
      { name: "twitter:description", content: "Guia para unificar canais com CRM e IA." },
      { name: "keywords", content: "estratégia omnichannel, plataforma omnichannel, omnichannel Brasil" },
    ],
    links: [{ rel: "canonical", href: `${SITE}/omnichannel` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Início", item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: "Omnichannel", item: `${SITE}/omnichannel` },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${SITE}/omnichannel#faq`,
          mainEntity: OMNICHANNEL_FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: OmnichannelPage,
});

function OmnichannelPage() {
  return (
    <>
      <section className="gradient-hero relative overflow-hidden pt-24 pb-16">
        <div className="circuit-pattern absolute inset-0 opacity-25" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <Breadcrumb items={[{ label: "Início", to: "/" }, { label: "Omnichannel" }]} />
          <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
            Estratégia <span className="gradient-text">Omnichannel</span>: como transformar seu negócio
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
            Em um mundo onde a linha entre online e offline desaparece, oferecer uma experiência unificada deixou de
            ser diferencial e virou necessidade. Veja o que é omnichannel e como aplicá-lo com a Kentro.
          </p>
        </div>
      </section>

      <article className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <Section title="O que é omnichannel?">
          <p>
            Omnichannel é a abordagem que oferece uma experiência contínua ao cliente, independentemente do canal.
            Todos os pontos de contato ficam interligados: o cliente pode começar uma conversa no Instagram, continuar
            no WhatsApp e finalizar no site — sem repetir informações.
          </p>
        </Section>

        <Section title="Multicanal × Omnichannel (qual a diferença?)">
          <p>
            No <strong>multicanal</strong>, os canais funcionam isolados e o cliente repete a história em cada um. No{" "}
            <strong>omnichannel</strong>, os canais conversam entre si — a empresa já sabe quem é o cliente, em que
            ponto da jornada está e o que ele precisa.
          </p>
        </Section>

        <Section title="Por que omnichannel é importante?">
          <ul className="my-4 list-disc space-y-2 pl-5 text-muted-foreground">
            <li>Clientes esperam ser atendidos no canal que preferem, sem perder a continuidade.</li>
            <li>Aumenta satisfação, fidelidade e, por consequência, as vendas.</li>
            <li>Reduz retrabalho da equipe e perda de contexto entre canais.</li>
          </ul>
        </Section>

        <Section title="Como a Kentro potencializa sua estratégia omnichannel">
          <div className="my-6 grid gap-4 md:grid-cols-3">
            <Card icon={<Layers className="size-5" />} title="Integração de canais">
              WhatsApp, Facebook, Instagram, Telegram, e-mail e voz centralizados em uma única plataforma.
            </Card>
            <Card icon={<Bot className="size-5" />} title="Inteligência artificial">
              Automatiza respostas, identifica padrões e personaliza o atendimento sem perder o tom humano.
            </Card>
            <Card icon={<Database className="size-5" />} title="CRM integrado">
              Todo o histórico do cliente à mão, com etiquetas e oportunidades, para um atendimento personalizado.
            </Card>
          </div>
        </Section>

        <Section title="Conclusão">
          <p>
            Com as ferramentas certas, implementar omnichannel é mais acessível do que parece. Em um mundo onde a
            experiência do cliente é tudo, o omnichannel é o caminho — e com a Kentro, esse futuro é agora.
          </p>
          <p className="mt-4 text-sm">
            Leia também:{" "}
            <Link to="/gestao-de-atendimento" className="text-primary-glow hover:underline">
              gestão de atendimento digital com IA
            </Link>{" "}
            e{" "}
            <Link to="/integracoes" className="text-primary-glow hover:underline">
              integrações disponíveis
            </Link>
            .
          </p>
        </Section>
      </article>

      {/* FAQ específica desta página — a lista completa vive em /perguntas-frequentes */}
      <section className="border-t border-white/10 bg-white/[0.015]">
        <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight md:text-4xl">Dúvidas sobre omnichannel</h2>
            <p className="mt-3 text-sm text-muted-foreground md:text-base">
              Perguntas específicas de quem vai unificar canais. Para dúvidas sobre planos, LGPD, integrações e
              segmentos, veja a{" "}
              <Link to="/perguntas-frequentes" className="text-primary-glow hover:underline">
                central de perguntas frequentes
              </Link>
              .
            </p>
          </div>

          <div className="mt-10">
            <Accordion type="multiple" defaultValue={OMNICHANNEL_FAQ.map((f) => f.id)} className="space-y-2">
              {OMNICHANNEL_FAQ.map((f) => (
                <AccordionItem
                  key={f.id}
                  value={f.id}
                  id={f.id}
                  className="scroll-mt-24 rounded-lg border border-white/10 bg-white/[0.02] px-4 data-[state=open]:bg-white/[0.04]"
                >
                  <AccordionTrigger className="py-3 text-left text-[15px] font-semibold hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-3 text-sm leading-relaxed text-muted-foreground">
                    <p>{f.a}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <p className="mt-8 text-center text-sm text-muted-foreground">
            <Link to="/perguntas-frequentes" className="text-primary-glow hover:underline">
              Ver todas as perguntas frequentes da Kentro
            </Link>
          </p>
        </div>
      </section>

      <FinalCta
        title="Quero unificar meus canais com a Kentro"
        subtitle="Receba um diagnóstico gratuito de como unificar seu atendimento."
      />
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10 first:mt-0">
      <h2 className="text-2xl font-bold tracking-tight md:text-3xl">{title}</h2>
      <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-muted-foreground [&_strong]:text-foreground">
        {children}
      </div>
    </section>
  );
}

